import { useEffect } from 'react';

import { useDMConversationStore } from '@auw/stores';
import { useDMStore } from '@auw/workspace-ui';

const DM_POLL_INTERVAL_MS = 15_000;

export interface UseDMUnreadPollingOptions {
  /** tenant feature flag — when off, the hook no-ops so on-prem builds
   *  without DM never contact the DM endpoints. */
  readonly messageModeEnabled: boolean;
  readonly hasSessionContext: boolean;
  readonly dmWsConnected: boolean;
  readonly nonCriticalReady: boolean;
}

/**
 * Sidebar's DM unread-count fallback poller.
 *
 * Keeps the sidebar badge fresh when the DM WebSocket can't connect
 * (flaky network, backend restarting, tenant without DM). Skips entirely
 * when the WS is healthy to avoid duplicating work — the WS already
 * streams unread updates — and pauses on tab hide so inactive tabs don't
 * hammer the API every 15 seconds.
 */
export function useDMUnreadPolling({
  messageModeEnabled,
  hasSessionContext,
  dmWsConnected,
  nonCriticalReady,
}: UseDMUnreadPollingOptions): void {
  useEffect(() => {
    if (!messageModeEnabled) return;
    if (!hasSessionContext || dmWsConnected || !nonCriticalReady) return;

    let timer: ReturnType<typeof setTimeout>;

    const syncUnread = () => {
      useDMStore.getState().loadUnreadCounts();
      useDMConversationStore.getState().loadConversations();
    };

    const poll = () => {
      syncUnread();
      timer = setTimeout(poll, DM_POLL_INTERVAL_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        poll();
      } else {
        clearTimeout(timer);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    poll();

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [messageModeEnabled, dmWsConnected, hasSessionContext, nonCriticalReady]);
}
